import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle } from "lucide-react";
import BrandMark from "../components/BrandMark";

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState({ phase: "checking", message: "Verifying payment…" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus({ phase: "error", message: "Missing session id" }); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/api/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await checkAuth();
          setStatus({ phase: "ok", message: `You're on the ${data.plan_id?.toUpperCase()} plan.` });
          return;
        }
        if (data.status === "expired") {
          setStatus({ phase: "error", message: "Checkout session expired." });
          return;
        }
        setAttempts(n);
        if (n >= 12) { setStatus({ phase: "pending", message: "Still processing… refresh later from Account." }); return; }
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setStatus({ phase: "error", message: "Failed to verify payment." });
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, checkAuth]);

  const isOk = status.phase === "ok";
  const isErr = status.phase === "error";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden" data-testid="checkout-return">
      <div className="ambient-orb" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex justify-center mb-7">
          <BrandMark size={44} />
        </Link>
        <div className="card-surface p-8 text-center">
          {isOk
            ? <CheckCircle2 className="w-14 h-14 text-[var(--bull)] mx-auto" style={{ filter: "drop-shadow(0 0 24px rgba(0,255,156,0.5))" }} />
            : isErr
              ? <XCircle className="w-14 h-14 text-[var(--bear)] mx-auto" />
              : <div className="w-14 h-14 mx-auto rounded-full border-2 border-white/70 border-t-transparent animate-spin"></div>}
          <h1 className="text-xl text-white font-semibold tracking-tight mt-5" data-testid="checkout-status-text">
            {status.message}
          </h1>
          {!isOk && !isErr && <div className="label-mono mt-2">Attempt {attempts + 1}/13</div>}
          <div className="mt-7 flex justify-center gap-2">
            <button onClick={() => navigate("/dashboard")} className="btn-primary" data-testid="checkout-go-dashboard">Go to dashboard</button>
            <button onClick={() => navigate("/pricing")} className="btn-ghost" data-testid="checkout-go-pricing">Pricing</button>
          </div>
        </div>
      </div>
    </div>
  );
}
